import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Calendar, TrendingUp, AlertCircle, Loader, RefreshCw, ChevronDown, ChevronUp, Radio, ArrowUp, ArrowDown, Minus, Zap, Target, Wallet } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { GOOSE_FIX_I_DEFAULTS, defaultTrendFilterMargin, warsawTime } from './GooseOrderBuilder';

// Same model lists as the Predictions tab (View Predictions section).
const FIX_I_MODELS = ['v1.0.0', 'v1.0.1', 'v1.0.2', 'v1.0.3', 'v1.0.3.1', 'v2.0.0', 'v2.0.0-cp14', 'v2.0.0-hourly', 'v2.0.0-l1', 'v2.0.0-narx', 'v2.0.0-narx-single'];
const FIX_II_MODELS = ['v1.0.0-fix2', 'v1.0.0-fix2-cp14', 'v2.0.0-l1-fix2', 'v2.0.0-narx-fix2'];
const PROB_MODELS = ['v9.0.0-daily', 'v3.0.0', 'v3.1.0', 'v3.1.1', 'v3.2.0', 'v3.3.0', 'v4.0.0', 'v4.1.0', 'v5.0.0', 'v11.0.0-tabpfn-3.5-daily-gpu', 'v30-k'];
export const COLLAPSE_MODELS = ['prod-v2.0.0', 'collapse_logreg_v1_wf_pse_till_500'];
export const SPIKE_MODELS = ['v9.0.0-upshift', 'prod-spike-v2.0.0', 'prod-spike-ra-pure', 'prod-spike-rz-champion', 'prod-spike-r3-champion'];

// Same as Predictions.js: skip buy/sell when Fix I vs Fix II diverge beyond the cutoff.
const PREDS_DIFF_CUTOFF = 1000;

// Parse one run's predictions CSV into hourly quantile rows + per-quantile averages.
const parseRunCsv = (content) => {
  if (!content || content === 'Predictions not available yet.') return null;
  const lines = content.trim().split('\n');
  if (lines.length <= 1) return null;

  const headers = lines[0].split(',').map(h => h.trim());
  const hourIdx = headers.indexOf('hour');
  const qCols = headers
    .map((h, i) => (h.startsWith('stacked_q') ? { name: h.replace('stacked_', ''), idx: i } : null))
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));
  if (hourIdx === -1 || qCols.length === 0) return null;

  const rows = [];
  for (const line of lines.slice(1)) {
    const parts = line.split(',');
    const hour = parseInt(parts[hourIdx], 10);
    if (isNaN(hour)) continue;
    const q = {};
    qCols.forEach(c => {
      const v = parseFloat(parts[c.idx]);
      q[c.name] = isNaN(v) ? null : v;
    });
    rows.push({ hour, q });
  }
  if (rows.length === 0) return null;
  rows.sort((a, b) => a.hour - b.hour);

  const avg = {};
  qCols.forEach(c => {
    const vals = rows.map(r => r.q[c.name]).filter(v => v != null);
    avg[c.name] = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  });

  let peak = null;
  rows.forEach(r => {
    if (r.q.q50 == null) return;
    if (!peak || r.q.q50 > peak.value) peak = { hour: r.hour, value: r.q.q50 };
  });

  return { rows, avg, peak, qNames: qCols.map(c => c.name) };
};

// Same as Predictions.js calculateDailyAverages, reduced to the prediction avg.
const fixCsvAvg = (content) => {
  if (!content || content === 'Predictions not available yet.') return null;
  const lines = content.trim().split('\n');
  if (lines.length <= 1) return null;
  const headers = lines[0].split(',');
  const predictionIndex = headers.indexOf('prediction');
  if (predictionIndex === -1) return null;
  let sum = 0;
  let count = 0;
  lines.slice(1).forEach(line => {
    const v = parseFloat(line.split(',')[predictionIndex]);
    if (!isNaN(v)) { sum += v; count++; }
  });
  return count > 0 ? sum / count : null;
};

// Europe/Warsaw UTC offset (ms) at a given instant, read from the IANA database
// so DST is handled without hardcoding +1/+2.
const warsawOffsetMs = (utcMs) => {
  const p = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Warsaw', hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(utcMs))
    .reduce((acc, part) => { acc[part.type] = part.value; return acc; }, {});
  const asIfUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
  return asIfUtc - utcMs;
};

// "YYMMDDHHMMSS" -> epoch ms. The run folder stamp is Warsaw wall-clock (the
// inference box runs Europe/Warsaw), so it must be read as Warsaw and NOT as the
// browser's zone: it is compared against issue_date, which is an absolute instant.
const runTsToMs = (ts) => {
  if (!ts || ts.length !== 12) return null;
  const asUtc = Date.UTC(2000 + parseInt(ts.slice(0, 2), 10), parseInt(ts.slice(2, 4), 10) - 1,
    parseInt(ts.slice(4, 6), 10), parseInt(ts.slice(6, 8), 10),
    parseInt(ts.slice(8, 10), 10), parseInt(ts.slice(10, 12), 10));
  if (isNaN(asUtc)) return null;
  // Two passes so the offset is taken at the instant we actually resolve to;
  // only matters within an hour of a DST switch.
  return asUtc - warsawOffsetMs(asUtc - warsawOffsetMs(asUtc));
};

// Pair each prob run with the spike issue it is priced with — the same rule as
// _priced_spike_pair in goose_bison_orders.py.
//
// A per-run engine (v9.0.0-upshift): a cycle writes its S3 run folder at the
// start of predict.py and its DB issue_date ~100 s later when the upshift
// commits, and flock -n guarantees cycles never overlap. So a run owns exactly
// the issues falling between its own stamp and the next run's stamp — an
// interval test, with no tolerance window and no offset to estimate. A run
// owning none is still waiting for its upshift (or never got one).
//
// A once-a-day engine issued before the session's first run
// (prod-spike-r3-champion, 09:30) leaves nothing to wait for: every run is
// paired with its latest issue.
//
// The previous nearest-match-plus-median-offset version mispaired silently
// whenever the upshift produced fewer issues than there were runs (2026-09-01:
// 17 runs, 4 issues, because build_training_data.py was failing on a missing
// de_realised_prev_day). The median gap was then dominated by runs that had no
// issue at all, tripped the 20-minute "timezone" guard, and slid every row by
// ~22 minutes — lighting runs that carried no shift, hiding the four that did,
// and re-lighting older rows as each new issue landed, which read as retroactive
// backfilling of settled runs.
const pairSpikeIssues = (runs, issues) => {
  const map = new Map();
  if (!issues.length || !runs.length) return map;

  const asc = runs.filter(r => r.timeMs != null).sort((a, b) => a.timeMs - b.timeMs);
  const issuesAsc = [...issues].sort((a, b) => a.timeMs - b.timeMs);
  if (!asc.length) return map;

  const newestIssue = issuesAsc[issuesAsc.length - 1];
  if (newestIssue.timeMs < asc[0].timeMs) {
    asc.forEach(run => map.set(run.run_id, newestIssue));
    return map;
  }

  asc.forEach((run, i) => {
    // The newest run stays open-ended: its issue is committed after its folder
    // appears, so an unbounded upper edge is what keeps pairing monotonic.
    const upper = i + 1 < asc.length ? asc[i + 1].timeMs : Infinity;
    // Last issue inside the window wins: a retry within one cycle supersedes
    // its earlier attempt.
    let own = null;
    issuesAsc.forEach(iss => {
      if (iss.timeMs >= run.timeMs && iss.timeMs < upper) own = iss;
    });
    if (own) map.set(run.run_id, own);
  });
  return map;
};

const fmt = (v, digits = 2) => (v == null || isNaN(v) ? '—' : v.toFixed(digits));

// "2026-08-24T14:37:05+02:00" -> "08-24 14:37". The API already returns Warsaw time.
const fmtTradeTs = (iso) => (iso && iso.length >= 16 ? `${iso.slice(5, 10)} ${iso.slice(11, 16)}` : null);

const LivePreds = () => {
  const { fetchGet, axiosPost } = useApi();

  // Inputs mirror the Predictions tab, with Live Preds defaults for the two live models.
  const [date, setDate] = useState('');
  const [fixIModel, setFixIModel] = useState('v2.0.0');
  const [fixIIModel, setFixIIModel] = useState('v1.0.0-fix2');
  const [probModel, setProbModel] = useState('v30-k');
  const [spread, setSpread] = useState(5);
  const [riskModel, setRiskModel] = useState('prod-v2.0.0');
  const [spikeModel, setSpikeModel] = useState('v9.0.0-upshift');

  // Same toggles and defaults as the Predictions tab.
  const [applyRidge, setApplyRidge] = useState(false);
  const [applyRisk, setApplyRisk] = useState(true);
  const [applySpikeRisk, setApplySpikeRisk] = useState(false);
  const [addOneHour, setAddOneHour] = useState(true);

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [expandedRuns, setExpandedRuns] = useState(() => new Set());

  // Default to tomorrow: the 9:30-10:32 runs predict next-day delivery.
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setDate(tomorrow.toISOString().split('T')[0]);
  }, []);

  const fetchData = useCallback(async (silent = false) => {
    if (!date) return;
    if (silent) setRefreshing(true); else { setLoading(true); setError(''); }

    try {
      // Spike engine read only while Apply Price Spike Risk is on.
      const spikeParam = applySpikeRisk ? `&spike_risk_model=${encodeURIComponent(spikeModel)}` : '';
      const response = await fetchGet(
        `/live-preds/${date}?prob_model=${encodeURIComponent(probModel)}${spikeParam}` +
        `&fix_i_model=${encodeURIComponent(fixIModel)}&fix_ii_model=${encodeURIComponent(fixIIModel)}` +
        `&risk_model=${encodeURIComponent(riskModel)}`
      );
      const json = await response.json();
      if (json.status === 'success') {
        setData(json);
        setError('');
        setLastUpdated(new Date());
      } else {
        setError(json.message || 'Failed to fetch live predictions');
      }
    } catch (err) {
      console.error('Error fetching live predictions:', err);
      setError('Network error. Failed to fetch live predictions.');
    } finally {
      if (silent) setRefreshing(false); else setLoading(false);
    }
  }, [date, probModel, applySpikeRisk, spikeModel, fixIModel, fixIIModel, riskModel, fetchGet]);

  // Load on mount and whenever the query changes.
  useEffect(() => { fetchData(false); }, [fetchData]);

  // Auto-refresh every 10 seconds.
  useEffect(() => {
    const id = setInterval(() => fetchData(true), 10000);
    return () => clearInterval(id);
  }, [fetchData]);

  const displayHour = useCallback((h) => (addOneHour ? h + 1 : h), [addOneHour]);

  // Latest-issue shift maps, same shape the Predictions tab works with.
  const ridgeShifts = data?.ridge?.status === 'success' ? data.ridge.shifts : null;
  const riskShifts = data?.risk_shift?.status === 'success' ? data.risk_shift.shifts : null;
  const riskScores = data?.risk_score?.status === 'success' ? data.risk_score.scores : null;

  // Newest-first list of runs enriched with parsed CSV, paired spike issue and deltas.
  const processedRuns = useMemo(() => {
    if (!data || !data.runs) return [];

    const runs = data.runs
      .map(run => ({ ...run, timeMs: runTsToMs(run.timestamp), parsed: parseRunCsv(run.content) }))
      .filter(r => r.parsed)
      .sort((a, b) => (b.timeMs || 0) - (a.timeMs || 0));

    const issues = (data.spike_issues || []).map(iss => ({
      ...iss,
      timeMs: new Date(iss.issue_date).getTime(),
    })).filter(iss => !isNaN(iss.timeMs));

    const spikeMap = pairSpikeIssues(runs, issues);

    return runs.map((run, i) => {
      const spike = spikeMap.get(run.run_id) || null;
      let spikeStats = null;
      if (spike) {
        // Spike-model figures only, for the "+X @ hN · Nh shifted" badge. The
        // shifted average is NOT built from these — it needs the downshift and
        // ridge shift too, so it goes through rowShiftFor instead.
        let maxShift = null;
        let maxShiftHour = null;
        let shiftedHours = 0;
        run.parsed.rows.forEach(r => {
          const s = spike.shifts[r.hour];
          const v = s == null ? 0 : s;
          if (v > 0) shiftedHours += 1;
          if (v > 0 && (maxShift == null || v > maxShift)) { maxShift = v; maxShiftHour = r.hour; }
        });
        spikeStats = { maxShift, maxShiftHour, shiftedHours };
      }

      const prev = runs[i + 1];
      const delta = prev && prev.parsed.avg.q50 != null && run.parsed.avg.q50 != null
        ? run.parsed.avg.q50 - prev.parsed.avg.q50
        : null;

      return { ...run, spike, spikeStats, delta };
    });
  }, [data]);

  const latest = processedRuns[0] || null;
  const oldest = processedRuns[processedRuns.length - 1] || null;
  // The run the price comes from. With Apply Price Spike Risk on it waits for
  // the spike engine: the newest run paired with a spike issue, so a run whose
  // upshift hasn't landed yet doesn't move the price. Off: the newest run.
  const pricedRun = applySpikeRisk ? (processedRuns.find(r => r.spike) || null) : latest;
  const waitingForSpike = applySpikeRisk && latest != null && pricedRun !== latest;
  const sessionDelta = pricedRun && oldest && pricedRun !== oldest
    && pricedRun.parsed.avg.q50 != null && oldest.parsed.avg.q50 != null
    ? pricedRun.parsed.avg.q50 - oldest.parsed.avg.q50
    : null;

  // DEPOSIT: cash the trading system blocks for the Fix I curves GooseOrderBuilder
  // would build — its defaults with BASE Idle, and this page's models and shifts. Computed once per
  // set of inputs, as soon as there is a run to price (with Apply Price Spike
  // Risk on, once its upshift has landed); later runs don't refresh it. A failed
  // attempt is retried when the next run arrives.
  const [deposit, setDeposit] = useState(null);
  const depositRequest = useRef(null);
  const depositKey = JSON.stringify([date, probModel, fixIModel, fixIIModel, applyRidge, applyRisk, riskModel, applySpikeRisk, spikeModel]);
  const pricedRunId = pricedRun ? pricedRun.run_id : null;
  useEffect(() => {
    if (!pricedRunId || depositRequest.current === depositKey) return;
    if (deposit?.key === depositKey && (deposit.status === 'success' || deposit.runId === pricedRunId)) return;
    depositRequest.current = depositKey;
    const settle = (result) => {
      setDeposit({ ...result, key: depositKey, runId: pricedRunId });
      if (depositRequest.current === depositKey) depositRequest.current = null;
    };
    axiosPost('/live-preds-deposit', {
      date,
      prob_model_version: probModel,
      fix_i_model: fixIModel,
      fix_ii_model: fixIIModel,
      trend_filter_margin: defaultTrendFilterMargin(probModel),
      bucket1_volume: GOOSE_FIX_I_DEFAULTS.bucket1Volume,
      bucket2_volume: GOOSE_FIX_I_DEFAULTS.bucket2Volume,
      bucket3_volume: GOOSE_FIX_I_DEFAULTS.bucket3Volume,
      apply_ridge_shift: applyRidge,
      apply_risk: applyRisk,
      risk_score_model: applyRisk ? riskModel : null,
      spike_risk_model: applySpikeRisk ? spikeModel : null,
    })
      .then(response => settle(response.data))
      .catch(err => settle({ status: 'error', message: err.message || 'Failed to compute deposit' }));
  }, [pricedRunId, depositKey, deposit, date, probModel, fixIModel, fixIIModel,
      applyRidge, applyRisk, riskModel, applySpikeRisk, spikeModel, axiosPost]);
  const depositShown = deposit?.key === depositKey ? deposit : null;

  // Per-hour combined shift for one run: ridge + collapse (down) + spike (up),
  // each gated by its toggle. Same composition goose_bison_orders applies to the
  // whole quantile stack before it reads the ladder. Spike comes from the issue
  // paired to this run, not the latest one, so a card stays self-consistent.
  // The two risk-engine shifts only: collapse (down) + spike (up). This is what
  // the shifted-average arrow reports — the ridge shift is deliberately not part
  // of it.
  const riskShiftFor = useCallback((run, hour) => (
    ((applyRisk && riskShifts && riskShifts[hour] != null) ? riskShifts[hour] : 0)
    + ((applySpikeRisk && run.spike && run.spike.shifts[hour] != null) ? run.spike.shifts[hour] : 0)
  ), [applyRisk, riskShifts, applySpikeRisk]);

  const rowShiftFor = useCallback((run, hour) => (
    ((applyRidge && ridgeShifts && ridgeShifts[hour] != null) ? ridgeShifts[hour] : 0)
    + riskShiftFor(run, hour)
  ), [applyRidge, ridgeShifts, riskShiftFor]);

  // What the risk engines did on the priced run: upshift = spike model,
  // downshift = collapse model.
  const riskActions = useMemo(() => {
    const summarize = (shifts, dir) => {
      if (!shifts) return null;
      let extreme = null;
      let hour = null;
      let count = 0;
      Object.entries(shifts).forEach(([h, v]) => {
        if (v == null) return;
        const active = dir === 'up' ? v > 0 : v < 0;
        if (!active) return;
        count++;
        if (extreme == null || (dir === 'up' ? v > extreme : v < extreme)) {
          extreme = v;
          hour = parseInt(h, 10);
        }
      });
      return { extreme, hour, count };
    };
    return { up: summarize(pricedRun?.spike?.shifts, 'up'), down: summarize(riskShifts, 'down') };
  }, [pricedRun, riskShifts]);

  // Buy/Sell — IDENTICAL to the Predictions tab: avg over hours of
  // (stacked_q50 + ridge/collapse/spike shifts per toggles) +/- Fix I spread,
  // with the Fix I vs Fix II diff skip rule.
  const buySell = useMemo(() => {
    if (!pricedRun) return null;

    let sum = 0;
    let count = 0;
    pricedRun.parsed.rows.forEach(r => {
      if (r.q.q50 == null) return;
      sum += r.q.q50 + rowShiftFor(pricedRun, r.hour);
      count++;
    });
    if (count === 0) return null;

    const predictionAvg = sum / count;
    const shiftUnavailable = applyRidge && (!ridgeShifts || Object.keys(ridgeShifts).length === 0);

    const fixIAvg = data?.fix_i?.status === 'success' ? fixCsvAvg(data.fix_i.content) : null;
    const fixIIAvg = data?.fix_ii?.status === 'success' ? fixCsvAvg(data.fix_ii.content) : null;
    let predsDiff = null;
    let skipBuy = false;
    let skipSell = false;
    if (fixIAvg != null && fixIIAvg != null && fixIIAvg !== 0) {
      predsDiff = ((fixIAvg - fixIIAvg) / fixIIAvg) * 100;
      if (predsDiff > PREDS_DIFF_CUTOFF) skipBuy = true;
      if (predsDiff < -PREDS_DIFF_CUTOFF) skipSell = true;
    }

    return {
      predictionAvg,
      buy: predictionAvg - spread,
      sell: predictionAvg + spread,
      shiftUnavailable,
      skipBuy,
      skipSell,
      predsDiff,
    };
  }, [pricedRun, data, applyRidge, ridgeShifts, rowShiftFor, spread]);

  const eurPlnRate = data?.eur_pln_rate || null;
  const toPln = (eur) => (eur != null && eurPlnRate ? eur * eurPlnRate : null);
  const toEur = (pln) => (pln != null && eurPlnRate ? pln / eurPlnRate : null);

  const toggleRun = (runId) => {
    setExpandedRuns(prevSet => {
      const next = new Set(prevSet);
      if (next.has(runId)) next.delete(runId); else next.add(runId);
      return next;
    });
  };

  const DeltaBadge = ({ value, digits = 2 }) => {
    if (value == null) return <span className="text-gray-400 text-xs">—</span>;
    const Icon = value > 0.005 ? ArrowUp : value < -0.005 ? ArrowDown : Minus;
    const cls = value > 0.005 ? 'text-green-700 bg-green-100' : value < -0.005 ? 'text-red-700 bg-red-100' : 'text-gray-600 bg-gray-100';
    return (
      <span className={`inline-flex items-center space-x-0.5 px-1.5 py-0.5 rounded text-xs font-semibold ${cls}`}>
        <Icon size={12} />
        <span>{value > 0 ? '+' : ''}{value.toFixed(digits)}</span>
      </span>
    );
  };

  const runTimeLabel = (run) => (run.created_at ? run.created_at.split(' ')[1] : run.timestamp);

  // Which run the price comes from; with Apply Price Spike Risk on, a newer run
  // still waiting for its spike issue is named next to it.
  const pricedNote = !pricedRun ? (
    <span className="text-amber-600">Waiting for {spikeModel}</span>
  ) : (
    <>
      Last issue at {runTimeLabel(pricedRun)}
      {waitingForSpike && <span className="text-amber-600"> · {runTimeLabel(latest)} waiting for {spikeModel}</span>}
    </>
  );

  const Toggle = ({ label, checked, onChange, accent = 'blue' }) => (
    <div className="flex items-center space-x-3">
      <span className="text-sm text-gray-700">{label}</span>
      <label className="relative inline-flex items-center cursor-pointer">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only peer" />
        <div className={`w-11 h-6 bg-gray-200 border border-gray-400 shadow-sm peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-${accent}-300 rounded-full peer peer-checked:border-${accent}-500 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border after:border-gray-400 after:shadow-sm after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-${accent}-600`}></div>
      </label>
    </div>
  );

  // Market info card: main currency big, the other one below it. By default the
  // feed's original currency is the main one; `primary` overrides (e.g. the f2
  // feed arrives in EUR but PLN is the price that matters).
  const MarketCard = ({ title, entry, border = 'border-blue-300', primary }) => {
    if (!entry) {
      return (
        <div className={`bg-white rounded-lg p-3 border ${border}`}>
          <p className="text-xs text-gray-500 uppercase">{title}</p>
          <p className="text-sm text-gray-400 mt-1">N/A</p>
        </div>
      );
    }
    const converted = entry.currency === 'EUR' ? toPln(entry.avg) : toEur(entry.avg);
    const convertedCur = entry.currency === 'EUR' ? 'PLN' : 'EUR';
    const originalIsMain = !primary || primary === entry.currency;
    const [mainVal, mainCur] = originalIsMain ? [entry.avg, entry.currency] : [converted, convertedCur];
    const [subVal, subCur] = originalIsMain ? [converted, convertedCur] : [entry.avg, entry.currency];
    return (
      <div className={`bg-white rounded-lg p-3 border ${border}`}>
        <p className="text-xs text-gray-500 uppercase">{title}</p>
        <p className="text-lg font-bold text-gray-800">
          {fmt(mainVal)} <span className="text-xs font-normal text-gray-500">{mainCur}</span>
        </p>
        <p className="text-sm text-gray-600">{fmt(subVal)} <span className="text-xs">{subCur}</span></p>
        <p className="text-[10px] text-gray-400 mt-0.5">
          {entry.hours}h{entry.live_hours > 0 ? ` · ${entry.live_hours}h live` : ''}
          {entry.latest_issue ? ` · ${new Date(entry.latest_issue).toLocaleTimeString('en-GB', { timeZone: 'Europe/Warsaw', hour: '2-digit', minute: '2-digit' })}` : ''}
        </p>
      </div>
    );
  };

  const market = data?.market || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
            <Radio className="w-6 h-6 text-purple-600" />
            <span>Live Preds</span>
          </h2>
          <p className="text-gray-600 mt-1">Intraday prediction runs, newest on top — one row per trigger</p>
        </div>
        <div className="flex items-center space-x-3 text-sm text-gray-500">
          {refreshing && <Loader className="w-4 h-4 animate-spin text-purple-500" />}
          <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
            <span>Auto-refresh 10s</span>
          </span>
          {lastUpdated && <span>Updated {lastUpdated.toLocaleTimeString('en-GB')}</span>}
        </div>
      </div>

      {/* Controls — same inputs as the Predictions tab */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
              <Calendar size={16} />
              <span>Date</span>
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
              <TrendingUp size={16} />
              <span>Fix I Model</span>
            </label>
            <select
              value={fixIModel}
              onChange={(e) => setFixIModel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {FIX_I_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
              <TrendingUp size={16} />
              <span>Fix II Model</span>
            </label>
            <select
              value={fixIIModel}
              onChange={(e) => setFixIIModel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {FIX_II_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
              <TrendingUp size={16} />
              <span>Probabilistic Model</span>
            </label>
            <select
              value={probModel}
              onChange={(e) => setProbModel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {PROB_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
              <Target size={16} />
              <span>Fix I Spread</span>
            </label>
            <input
              type="number"
              value={spread}
              onChange={(e) => setSpread(parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className={`flex items-center space-x-2 text-sm font-medium mb-2 ${!applyRisk ? 'text-gray-400' : 'text-gray-700'}`}>
              <AlertCircle size={16} />
              <span>Price Collapse Risk Engine</span>
            </label>
            <select
              value={riskModel}
              onChange={(e) => setRiskModel(e.target.value)}
              disabled={!applyRisk}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${!applyRisk ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
            >
              {COLLAPSE_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className={`flex items-center space-x-2 text-sm font-medium mb-2 ${!applySpikeRisk ? 'text-gray-400' : 'text-gray-700'}`}>
              <AlertCircle size={16} />
              <span>Price Spike Risk Engine</span>
            </label>
            <select
              value={spikeModel}
              onChange={(e) => setSpikeModel(e.target.value)}
              disabled={!applySpikeRisk}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${!applySpikeRisk ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
            >
              {SPIKE_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => fetchData(false)}
              disabled={loading || !date}
              className={`w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                loading || !date ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {loading ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Toggles — same set and defaults as the Predictions tab */}
        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2">
          <Toggle label="Apply Shift" checked={applyRidge} onChange={setApplyRidge} />
          <Toggle label="Apply Price Collapse Risk" checked={applyRisk} onChange={setApplyRisk} />
          <Toggle label="Apply Price Spike Risk" checked={applySpikeRisk} onChange={setApplySpikeRisk} accent="amber" />
          <Toggle label="Display hours as 1-24" checked={addOneHour} onChange={setAddOneHour} />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
          <div>
            <p className="text-red-800 font-medium">Error loading live predictions</p>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* DEPOSIT — cash blocked for the Fix I curve of the GooseOrderBuilder defaults, priced once.
          Shown before the day's first run too, with a dash in place of the amount. */}
      {date && (
        <div className="bg-white border border-gray-200 rounded-lg px-4 py-2.5 flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <span className="flex items-center space-x-1.5 text-xs font-semibold uppercase text-gray-500">
            <Wallet size={14} />
            <span>Deposit</span>
          </span>
          {depositShown?.status !== 'success' && (
            <span className="text-lg font-bold text-gray-400">—</span>
          )}
          {depositShown?.status === 'success' ? (
            <>
              <span className="text-lg font-bold text-gray-800">
                {Math.round(depositShown.deposit).toLocaleString('pl-PL')} <span className="text-xs font-normal text-gray-500">PLN</span>
              </span>
              <span className="text-xs text-gray-500">
                Fix I curve worst case {Math.round(depositShown.exposure).toLocaleString('pl-PL')}
                {' '}+ fees {depositShown.fees.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                {' '}· {fmt(depositShown.buy_volume_mwh, 0)} MWh
                {depositShown.prob_issue_date && <> · run {warsawTime(depositShown.prob_issue_date)}</>}
                {' '}· BASE {depositShown.base_action}
                {' '}· GooseOrderBuilder defaults, not refreshed
              </span>
            </>
          ) : depositShown?.status === 'error' ? (
            <span className="text-xs text-red-600">{depositShown.message}</span>
          ) : (
            <span className="text-xs text-gray-400">
              {pricedRun
                ? 'calculating…'
                : latest
                  ? `waiting for ${spikeModel}`
                  : loading && !data ? 'loading…' : `no ${probModel} run for ${date} yet`}
            </span>
          )}
        </div>
      )}

      {/* Buy/Sell + priced run summary */}
      {latest && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {buySell?.shiftUnavailable ? (
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-300">
              <p className="text-xs text-yellow-700 font-medium uppercase">Buy Price</p>
              <p className="text-sm text-yellow-600 mt-1">Ridge shift not available</p>
            </div>
          ) : buySell?.skipBuy ? (
            <div className="bg-red-50 rounded-lg p-4 border border-red-300">
              <p className="text-xs text-red-600 font-medium uppercase">Buy Price - Skipped</p>
              <p className="text-xs text-red-500 mt-1">Fix I vs Fix II diff: {buySell.predsDiff?.toFixed(2)}%</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg p-4 border border-green-300">
              <p className="text-xs text-gray-500 uppercase">Buy Price</p>
              <p className="text-2xl font-bold text-green-600">{buySell ? buySell.buy.toFixed(2) : '—'}</p>
              <p className="text-xs text-gray-400 mt-1">{pricedNote}</p>
            </div>
          )}
          {buySell?.shiftUnavailable ? (
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-300">
              <p className="text-xs text-yellow-700 font-medium uppercase">Sell Price</p>
              <p className="text-sm text-yellow-600 mt-1">Ridge shift not available</p>
            </div>
          ) : buySell?.skipSell ? (
            <div className="bg-red-50 rounded-lg p-4 border border-red-300">
              <p className="text-xs text-red-600 font-medium uppercase">Sell Price - Skipped</p>
              <p className="text-xs text-red-500 mt-1">Fix I vs Fix II diff: {buySell.predsDiff?.toFixed(2)}%</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg p-4 border border-red-300">
              <p className="text-xs text-gray-500 uppercase">Sell Price</p>
              <p className="text-2xl font-bold text-red-600">{buySell ? buySell.sell.toFixed(2) : '—'}</p>
              <p className="text-xs text-gray-400 mt-1">{pricedNote}</p>
            </div>
          )}
          <div className="bg-white rounded-lg p-4 border border-purple-300">
            <p className="text-xs text-gray-500 uppercase">Prediction Avg</p>
            <div className="flex items-baseline space-x-2 mt-1">
              <p className="text-2xl font-bold text-purple-700">{buySell ? buySell.predictionAvg.toFixed(2) : '—'}</p>
              <DeltaBadge value={pricedRun?.delta} />
            </div>
            <p className="text-xs text-gray-400 mt-1">
              after shifts · raw Q50: {fmt(pricedRun?.parsed.avg.q50)} · Session: {sessionDelta != null ? `${sessionDelta > 0 ? '+' : ''}${sessionDelta.toFixed(2)}` : '—'} · {processedRuns.length} runs
            </p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-amber-300">
            <p className="text-xs text-gray-500 uppercase">Risk Actions</p>
            <div className="mt-1 space-y-1 text-sm">
              {!applySpikeRisk ? (
                <p className="text-gray-400">Upshift: off</p>
              ) : riskActions.up == null ? (
                <p className="text-amber-600">Upshift: waiting for {spikeModel}</p>
              ) : riskActions.up.count > 0 ? (
                <p className="text-amber-700 font-semibold">
                  Upshift: +{fmt(riskActions.up.extreme)} max @ h{displayHour(riskActions.up.hour)} · {riskActions.up.count}h
                </p>
              ) : (
                <p className="text-gray-500">Upshift: not activated</p>
              )}
              {!applyRisk ? (
                <p className="text-gray-400">Downshift: off</p>
              ) : riskActions.down == null ? (
                <p className="text-gray-400">Downshift: no data</p>
              ) : riskActions.down.count > 0 ? (
                <p className="text-red-600 font-semibold">
                  Downshift: {fmt(riskActions.down.extreme)} max @ h{displayHour(riskActions.down.hour)} · {riskActions.down.count}h
                </p>
              ) : (
                <p className="text-gray-500">Downshift: not activated</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Market info — union of hourly + 15-min live-feed tables */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <MarketCard title="Avg Meteo DE" entry={market.meteo_de} />
        <MarketCard title="Avg Volue DE" entry={market.volue_de} border="border-indigo-300" />
        <div className="bg-white rounded-lg p-3 border border-blue-300">
          <p className="text-xs text-gray-500 uppercase">EEX Last Trade</p>
          {data?.eex_last_trade ? (
            <div>
              <p className="text-lg font-bold text-gray-800">
                {fmt(data.eex_last_trade.eex_last_eur)} <span className="text-xs font-normal text-gray-500">EUR</span>
              </p>
              <p className="text-sm text-gray-600">{fmt(data.eex_last_trade.eex_last_pln)} <span className="text-xs">PLN</span></p>
              <p className="text-[10px] text-gray-400 mt-0.5">
                offset: {data.eex_last_trade.rolling_offset_used ?? '—'}
                {fmtTradeTs(data.eex_last_trade.trade_ts) ? ` · ${fmtTradeTs(data.eex_last_trade.trade_ts)}` : ''}
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-400 mt-1">N/A</p>
          )}
        </div>
        <MarketCard title="Meteo Fix I Avg PL" entry={market.meteo_fix1_pl} border="border-emerald-300" />
        <MarketCard title="Meteo Fix II Avg PL" entry={market.meteo_fix2_pl} border="border-emerald-300" primary="PLN" />
        <MarketCard title="Volue Avg PL" entry={market.volue_pl} border="border-indigo-300" />
      </div>

      {/* Runs list */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="bg-purple-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">
            Prediction Runs {date && <span className="text-sm font-normal text-gray-600">for {date}</span>}
          </h3>
          {data?.spike_risk_model && (
            <span className="text-xs text-gray-600">
              Spike: {data.spike_risk_model} ({(data.spike_issues || []).length} issues)
            </span>
          )}
        </div>

        {loading && !data ? (
          <div className="text-center py-12 text-gray-500">
            <Loader className="w-8 h-8 mx-auto mb-3 animate-spin" />
            <p>Loading prediction runs...</p>
          </div>
        ) : processedRuns.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Radio className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p>No prediction runs found for {probModel} on {date}</p>
            <p className="text-xs mt-1 text-gray-400">Runs appear here as soon as they are published</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {processedRuns.map((run, i) => {
              const isExpanded = expandedRuns.has(run.run_id);
              // Average the per-hour Q50s carrying BOTH risk shifts — collapse
              // (down) and spike (up), each gated by its toggle. This used to add
              // spikeStats.avgShift, the spike model alone: the arrow showed only
              // the upshift, ignored the downshift, ignored the toggles, vanished
              // entirely on a run with a downshift but no spike issue, and divided
              // by every row while avg.q50 divides by the non-null ones.
              let shiftedAvg = null;
              if (run.parsed.avg.q50 != null) {
                let shiftedSum = 0;
                let shiftedCount = 0;
                run.parsed.rows.forEach(r => {
                  if (r.q.q50 == null) return;
                  shiftedSum += r.q.q50 + riskShiftFor(run, r.hour);
                  shiftedCount += 1;
                });
                if (shiftedCount) shiftedAvg = shiftedSum / shiftedCount;
              }
              let shownPeak = null;
              run.parsed.rows.forEach(r => {
                if (r.q.q50 == null) return;
                const v = r.q.q50 + rowShiftFor(run, r.hour);
                if (!shownPeak || v > shownPeak.value) shownPeak = { hour: r.hour, value: v };
              });
              const anyShift = run.parsed.rows.some(r => rowShiftFor(run, r.hour) !== 0);
              return (
                <div key={run.run_id}>
                  {/* Collapsed one-line summary */}
                  <button
                    onClick={() => toggleRun(run.run_id)}
                    className={`w-full px-4 py-2.5 flex items-center gap-4 text-left hover:bg-gray-50 transition-colors ${i === 0 ? 'bg-purple-50/40' : ''}`}
                  >
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />}

                    <div className="w-28 shrink-0">
                      <span className="font-mono text-sm text-gray-800">{runTimeLabel(run)}</span>
                      {i === 0 && (
                        <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-600 text-white align-middle">LATEST</span>
                      )}
                    </div>

                    <div className="w-40 shrink-0">
                      <span className="text-xs text-gray-400 uppercase mr-1">Q50</span>
                      <span className="text-base font-bold text-purple-700">{fmt(run.parsed.avg.q50)}</span>
                      {shiftedAvg != null && Math.abs(shiftedAvg - run.parsed.avg.q50) >= 0.005 && (
                        <span className="text-sm font-semibold text-amber-600 ml-1">→ {fmt(shiftedAvg)}</span>
                      )}
                    </div>

                    <div className="w-24 shrink-0">
                      <DeltaBadge value={run.delta} />
                    </div>

                    <div className="w-40 shrink-0 text-sm text-gray-600">
                      <span className="text-xs text-gray-400 uppercase mr-1">Q10–Q90</span>
                      {fmt(run.parsed.avg.q10, 0)}–{fmt(run.parsed.avg.q90, 0)}
                    </div>

                    <div className="w-40 shrink-0 text-sm text-gray-600">
                      <span className="text-xs text-gray-400 uppercase mr-1">Peak</span>
                      {run.parsed.peak ? <>h{displayHour(run.parsed.peak.hour)} · {fmt(run.parsed.peak.value, 0)}</> : '—'}
                    </div>

                    {applySpikeRisk && (
                      <div className="flex-1 text-sm">
                        {run.spikeStats ? (
                          run.spikeStats.maxShift != null ? (
                            <span className="text-amber-700 font-medium">
                              <Zap size={13} className="inline -mt-0.5 mr-1" />
                              +{fmt(run.spikeStats.maxShift, 1)} @ h{displayHour(run.spikeStats.maxShiftHour)}
                              <span className="text-gray-400 font-normal"> · {run.spikeStats.shiftedHours}h shifted</span>
                            </span>
                          ) : (
                            <span className="text-gray-400">no spike shift</span>
                          )
                        ) : !pricedRun || run.timeMs > pricedRun.timeMs ? (
                          <span className="text-amber-600">waiting for {spikeModel}</span>
                        ) : (
                          <span className="text-gray-300">no spike data</span>
                        )}
                      </div>
                    )}
                  </button>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="px-6 pb-4 bg-gray-50 border-t border-gray-100">
                      <div className="flex items-center justify-between py-2 text-xs text-gray-500">
                        <span className="font-mono">{run.run_id}</span>
                        <span className={anyShift ? 'text-amber-700' : ''}>
                          {anyShift
                            ? 'Quantiles include the applied shifts — hover a cell for the raw value'
                            : 'Raw quantiles — no shift applied'}
                        </span>
                        {run.spike && (
                          <span>
                            Spike issue: {new Date(run.spike.issue_date).toLocaleTimeString('en-GB', { timeZone: 'Europe/Warsaw' })} (Warsaw)
                          </span>
                        )}
                      </div>
                      <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="bg-gray-100 text-gray-600">
                              <th className="px-3 py-2 text-left font-medium">Hour</th>
                              {run.parsed.qNames.map(qn => (
                                <th key={qn} className={`px-3 py-2 text-right font-medium ${qn === 'q50' ? 'text-purple-700' : ''}`}>
                                  {qn.toUpperCase()}
                                </th>
                              ))}
                              {applyRisk && (
                                <>
                                  <th className="px-3 py-2 text-right font-medium">Down Shift</th>
                                  <th className="px-3 py-2 text-right font-medium">Down Risk</th>
                                </>
                              )}
                              {applySpikeRisk && (
                                <>
                                  <th className="px-3 py-2 text-right font-medium text-amber-700">Up Shift</th>
                                  <th className="px-3 py-2 text-right font-medium text-amber-700">Up Risk</th>
                                </>
                              )}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {run.parsed.rows.map(r => {
                              const isPeak = shownPeak && r.hour === shownPeak.hour;
                              const rowShift = rowShiftFor(run, r.hour);
                              const downShift = (applyRisk && riskShifts && riskShifts[r.hour] != null) ? riskShifts[r.hour] : null;
                              const downRisk = (applyRisk && riskScores && riskScores[r.hour] != null) ? riskScores[r.hour] : null;
                              const upShift = (applySpikeRisk && run.spike && run.spike.shifts[r.hour] != null) ? run.spike.shifts[r.hour] : null;
                              const upRisk = (applySpikeRisk && run.spike && run.spike.scores[r.hour] != null) ? run.spike.scores[r.hour] : null;
                              return (
                                <tr key={r.hour} className={isPeak ? 'bg-amber-50' : ''}>
                                  <td className="px-3 py-1.5 font-mono text-gray-700">{displayHour(r.hour)}</td>
                                  {run.parsed.qNames.map(qn => {
                                    const raw = r.q[qn];
                                    const shown = raw == null ? null : raw + rowShift;
                                    return (
                                      <td
                                        key={qn}
                                        title={(raw != null && rowShift !== 0)
                                          ? `raw ${fmt(raw)} ${rowShift > 0 ? '+' : '−'} ${fmt(Math.abs(rowShift))}`
                                          : undefined}
                                        className={`px-3 py-1.5 text-right ${qn === 'q50' ? 'font-semibold text-purple-700' : 'text-gray-600'} ${rowShift !== 0 ? 'underline decoration-dotted decoration-gray-300 underline-offset-4' : ''}`}
                                      >
                                        {fmt(shown)}
                                      </td>
                                    );
                                  })}
                                  {applyRisk && (
                                    <>
                                      <td className={`px-3 py-1.5 text-right ${downShift != null && downShift < 0 ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
                                        {downShift != null ? downShift.toFixed(2) : '-'}
                                      </td>
                                      <td className={`px-3 py-1.5 text-right ${downRisk != null && downRisk < 0 ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
                                        {downRisk != null ? downRisk.toFixed(2) : '-'}
                                      </td>
                                    </>
                                  )}
                                  {applySpikeRisk && (
                                    <>
                                      <td className={`px-3 py-1.5 text-right ${upShift != null && upShift > 0 ? 'text-amber-700 font-medium' : 'text-gray-400'}`}>
                                        {upShift != null ? upShift.toFixed(2) : '-'}
                                      </td>
                                      <td className={`px-3 py-1.5 text-right ${upRisk != null && upRisk > 0 ? 'text-amber-700 font-medium' : 'text-gray-400'}`}>
                                        {upRisk != null ? upRisk.toFixed(2) : '-'}
                                      </td>
                                    </>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LivePreds;
